import React, { useState, useEffect, useRef } from 'react';
import { FiTarget, FiEye, FiMapPin, FiAward, FiGlobe } from 'react-icons/fi';

const AboutOverview = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [text, setText] = useState('');
    const sectionRef = useRef(null);
    const fullText =
        'Empowering engineers, students, and professionals with world-class training in Bentley infrastructure design and analysis software.';

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) setIsVisible(true);
                });
            },
            { threshold: 0.12 }
        );
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => { if (sectionRef.current) observer.unobserve(sectionRef.current); };
    }, []);

    useEffect(() => {
        let i = 0;
        const typingInterval = setInterval(() => {
            if (i < fullText.length) {
                setText(fullText.substring(0, i + 1));
                i++;
            } else {
                clearInterval(typingInterval);
            }
        }, 45);
        return () => clearInterval(typingInterval);
    }, []);

    const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=Plus+Jakarta+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400&display=swap');

    *, *::before, *::after { box-sizing: border-box; }

    :root {
      --teal: #356575;
      --teal-dark: #2a4f5e;
      --teal-deep: #1e3a46;
      --teal-light: rgba(53,101,117,0.12);
      --white: #ffffff;
      --off-white: #f7f9fb;
      --text-dark: #18262d;
      --text-mid: #3d5a65;
      --text-muted: #7a9ba6;
      --border: rgba(53,101,117,0.18);
    }

    .bti-wrap {
      position: relative;
      padding: 7rem 0;
      background: var(--white);
      font-family: 'Plus Jakarta Sans', sans-serif;
      overflow: hidden;
    }

    .bti-container {
      max-width: 1320px;
      margin: 0 auto;
      padding: 0 2rem;
      position: relative;
      z-index: 2;
    }

    /* Header */
    .bti-head {
      text-align: center;
      max-width: 780px;
      margin: 0 auto 5rem;
      opacity: 0;
      transform: translateY(24px);
      transition: opacity 0.8s ease, transform 0.8s ease;
    }
    .bti-wrap.visible .bti-head { opacity: 1; transform: translateY(0); }

    .bti-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.45rem 1.1rem;
      background: var(--teal-light);
      border: 1px solid var(--border);
      border-radius: 100px;
      font-size: 0.8rem;
      font-weight: 600;
      color: var(--teal);
      letter-spacing: 0.06em;
      text-transform: uppercase;
      margin-bottom: 1.5rem;
    }
    .bti-badge-dot {
      width: 6px; height: 6px;
      border-radius: 50%;
      background: var(--teal);
      animation: pulseDot 2s ease-in-out infinite;
    }
    @keyframes pulseDot {
      0%,100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.5; transform: scale(1.5); }
    }

    .bti-h2 {
      font-family: 'Outfit', sans-serif;
      font-size: 2.4rem;
      font-weight: 700;
      line-height: 1.15;
      letter-spacing: -0.02em;
      color: #29354d;
      margin-bottom: 0.5rem;
    }
    .bti-h2 span { color: var(--teal); }

    .bti-line {
      width: 60px; height: 2px;
      background: linear-gradient(90deg, var(--teal), transparent);
      margin: 1.5rem auto;
      border-radius: 2px;
      transform: scaleX(0);
      transform-origin: center;
      transition: transform 1s ease 0.3s;
    }
    .bti-wrap.visible .bti-line { transform: scaleX(1); }

    .bti-typed {
      font-size: 1rem;
      line-height: 1.7;
      color: var(--text-mid);
      min-height: 54px;
    }
    .bti-cursor {
      display: inline-block;
      width: 2px; height: 1rem;
      background: var(--teal);
      margin-left: 3px;
      animation: blink 1s infinite;
    }
    @keyframes blink { 0%,100%{opacity:1;} 50%{opacity:0;} }

    .bti-sub {
      font-size: 0.95rem;
      line-height: 1.7;
      color: #6b7280;
      margin-top: 1.25rem;
    }

    .bti-chips {
      display: flex; flex-wrap: wrap; justify-content: center;
      gap: 0.6rem; margin-top: 1.75rem;
    }
    .bti-chip {
      display: inline-flex; align-items: center; gap: 0.4rem;
      padding: 0.5rem 1rem;
      border: 1px solid var(--border);
      border-radius: 100px;
      background: var(--off-white);
      font-size: 0.78rem; font-weight: 600;
      color: var(--teal);
      transition: background 0.3s, transform 0.3s;
    }
    .bti-chip:hover { background: var(--teal-light); transform: translateY(-2px); }

    /* Overview grid */
    .bti-overview {
      display: grid;
      grid-template-columns: 0.85fr 1.15fr;
      gap: 3.5rem;
      align-items: stretch;
      margin-bottom: 5rem;
      opacity: 0;
      transform: translateY(30px);
      transition: opacity 0.8s ease 0.15s, transform 0.8s ease 0.15s;
    }
    .bti-wrap.visible .bti-overview { opacity: 1; transform: translateY(0); }

    .bti-frame {
      position: relative;
      border-radius: 20px;
      overflow: hidden;
      border: 1.5px solid var(--border);
      box-shadow: 0 4px 24px rgba(53,101,117,0.08);
    }
    .bti-frame img {
      width: 100%; height: 100%;
      object-fit: cover;
      display: block;
      min-height: 380px;
    }
    .bti-frame-corner {
      position: absolute; width: 22px; height: 22px; z-index: 2;
    }
    .bti-frame-corner.tl { top: 12px; left: 12px; border-top: 2px solid var(--white); border-left: 2px solid var(--white); }
    .bti-frame-corner.br { bottom: 12px; right: 12px; border-bottom: 2px solid var(--white); border-right: 2px solid var(--white); }
    .bti-frame-cap {
      position: absolute; left: 14px; bottom: 14px;
      padding: 0.4rem 0.9rem;
      background: rgba(30,58,70,0.75);
      backdrop-filter: blur(6px);
      border-radius: 100px;
      color: var(--white);
      font-size: 0.72rem; font-weight: 600; letter-spacing: 0.04em;
    }

    .bti-panel {
      display: flex; flex-direction: column; gap: 1.5rem;
    }

    .bti-metrics {
      border: 1.5px solid var(--border);
      border-radius: 16px;
      overflow: hidden;
      background: var(--off-white);
    }
    .bti-metrics-head {
      display: flex; justify-content: space-between; align-items: center;
      background: linear-gradient(135deg, var(--teal) 0%, var(--teal-dark) 100%);
      color: var(--white);
      padding: 0.75rem 1.25rem;
      font-size: 0.72rem; font-weight: 700;
      letter-spacing: 0.08em; text-transform: uppercase;
    }
    .bti-metrics-grid {
      display: grid; grid-template-columns: repeat(3, 1fr);
    }
    .bti-metric {
      padding: 1.25rem; text-align: center;
      border-right: 1px solid var(--border);
    }
    .bti-metric:last-child { border-right: none; }
    .bti-metric-num {
      font-family: 'Outfit', sans-serif;
      font-size: 1.7rem; font-weight: 800;
      color: var(--teal); display: block; margin-bottom: 0.3rem;
    }
    .bti-metric-label {
      font-size: 0.72rem; font-weight: 600;
      color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.05em;
    }

    .bti-notes {
      border-left: 3px solid var(--teal);
      background: var(--off-white);
      padding: 1.5rem;
      border-radius: 0 16px 16px 0;
    }
    .bti-notes p {
      font-size: 0.95rem; line-height: 1.75;
      color: var(--text-mid);
      margin-bottom: 1rem;
    }
    .bti-notes p:last-child { margin-bottom: 0; }
    .bti-notes strong, .bti-notes b { color: var(--teal); font-weight: 700; }

    /* Annotations */
    .bti-annotations {
      display: grid; grid-template-columns: repeat(3, 1fr);
      gap: 1.75rem; margin-bottom: 5rem;
    }
    .bti-note-card {
      opacity: 0; transform: translateY(24px);
      padding: 1.75rem;
      border: 1.5px solid var(--border);
      border-radius: 18px;
      background: var(--white);
      transition: opacity 0.6s ease, transform 0.6s ease, box-shadow 0.3s ease, border-color 0.3s ease;
    }
    .bti-wrap.visible .bti-note-card { opacity: 1; transform: translateY(0); }
    .bti-wrap.visible .bti-note-card:nth-child(1) { transition-delay: 0.15s; }
    .bti-wrap.visible .bti-note-card:nth-child(2) { transition-delay: 0.28s; }
    .bti-wrap.visible .bti-note-card:nth-child(3) { transition-delay: 0.41s; }
    .bti-note-card:hover {
      border-color: var(--teal);
      box-shadow: 0 12px 30px rgba(53,101,117,0.14);
      transform: translateY(-4px);
    }
    .bti-note-idx {
      font-family: 'Outfit', sans-serif;
      font-size: 0.78rem; font-weight: 700;
      color: var(--teal);
      background: var(--teal-light);
      display: inline-flex; align-items: center; justify-content: center;
      width: 32px; height: 32px; border-radius: 10px;
      margin-bottom: 1rem;
    }
    .bti-note-card h3 {
      font-family: 'Outfit', sans-serif;
      font-size: 1.1rem; font-weight: 700;
      color: var(--text-dark); margin-bottom: 0.5rem;
    }
    .bti-note-card p {
      font-size: 0.9rem; line-height: 1.65; color: var(--text-muted);
    }

    /* Mission / Vision */
    .bti-mv-grid {
      display: grid; grid-template-columns: 1fr 1fr; gap: 2rem;
    }
    .bti-mv-panel {
      border-radius: 20px; overflow: hidden;
      opacity: 0; transform: translateY(28px);
      transition: opacity 0.7s ease, transform 0.7s ease;
    }
    .bti-wrap.visible .bti-mv-panel { opacity: 1; transform: translateY(0); }
    .bti-wrap.visible .bti-mv-panel:nth-child(1) { transition-delay: 0.2s; }
    .bti-wrap.visible .bti-mv-panel:nth-child(2) { transition-delay: 0.35s; }

    .bti-mv-panel.dark {
      background: linear-gradient(145deg, var(--teal-deep) 0%, var(--teal-dark) 100%);
      color: var(--white);
      padding: 2.25rem;
      border: 1.5px solid var(--teal);
    }
    .bti-mv-head {
      display: flex; align-items: center; gap: 0.75rem; margin-bottom: 1.25rem;
    }
    .bti-mv-icon {
      width: 42px; height: 42px; border-radius: 12px;
      background: rgba(255,255,255,0.12);
      border: 1px solid rgba(255,255,255,0.22);
      display: flex; align-items: center; justify-content: center;
      font-size: 1.1rem; flex-shrink: 0;
    }
    .bti-mv-title {
      font-family: 'Outfit', sans-serif;
      font-size: 1.2rem; font-weight: 700; letter-spacing: -0.01em;
    }
    .bti-mv-panel.dark p {
      font-size: 0.92rem; line-height: 1.75;
      color: rgba(255,255,255,0.75);
      margin-bottom: 1.5rem; font-weight: 300;
    }
    .bti-mv-list { display: flex; flex-direction: column; gap: 0.65rem; }
    .bti-mv-row {
      display: flex; align-items: center; gap: 0.65rem;
      font-size: 0.875rem; font-weight: 500;
      color: rgba(255,255,255,0.9);
    }
    .bti-mv-check {
      width: 18px; height: 18px; border-radius: 5px;
      background: rgba(255,255,255,0.12);
      border: 1px solid rgba(255,255,255,0.22);
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0;
    }
    .bti-mv-check svg { width: 10px; height: 10px; color: #5eead4; }

    /* Responsive */
    @media (max-width: 1024px) {
      .bti-overview { grid-template-columns: 1fr; }
      .bti-frame img { min-height: 300px; }
      .bti-metrics-grid { grid-template-columns: 1fr; }
      .bti-metric { border-right: none; border-bottom: 1px solid var(--border); }
      .bti-metric:last-child { border-bottom: none; }
    }
    @media (max-width: 900px) {
      .bti-annotations { grid-template-columns: 1fr; }
      .bti-mv-grid { grid-template-columns: 1fr; }
    }
    @media (max-width: 620px) {
      .bti-h2 { font-size: 1.9rem; }
      .bti-wrap { padding: 4.5rem 0; }
      .bti-container { padding: 0 1.25rem; }
    }

    @media (prefers-reduced-motion: reduce) {
      *, *::before, *::after {
        animation-duration: 0.01ms !important;
        transition-duration: 0.01ms !important;
      }
    }
  `;

    const CheckIcon = () => (
        <svg viewBox="0 0 10 10" fill="none">
            <path d="M2 5l2.5 2.5L8 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    return (
        <>
            <style>{styles}</style>
            <section ref={sectionRef} className={`bti-wrap${isVisible ? ' visible' : ''}`}>
                <div className="bti-container">

                    {/* Header */}
                    <div className="bti-head">
                        <div className="bti-badge">
                            <span className="bti-badge-dot" />
                            About Us
                        </div>
                        <h2 className="bti-h2">About <span>Bentley Training Center</span></h2>
                        <div className="bti-line" />
                        <p className="bti-typed">
                            {text}
                            <span className="bti-cursor" />
                        </p>
                        <p className="bti-sub">
                            Based in Bangalore, we are recognized as a Bentley Systems Authorized Training,
                            Certification &amp; Academic Partner — bringing infrastructure engineering
                            education to students and working professionals across roads and bridges,
                            rail and transit, water and wastewater, and public works.
                        </p>
                        <div className="bti-chips">
                            <span className="bti-chip"><FiMapPin /> Bangalore, India</span>
                            <span className="bti-chip"><FiAward /> Bentley Authorized Partner</span>
                            <span className="bti-chip"><FiGlobe /> Infrastructure Engineering Software</span>
                        </div>
                    </div>

                    {/* Overview */}
                    <div className="bti-overview">
                        <div className="bti-frame">
                            <img src="/images/fllorimage.avif" alt="Bentley Training" />
                            <span className="bti-frame-corner tl" aria-hidden="true"></span>
                            <span className="bti-frame-corner br" aria-hidden="true"></span>
                            <span className="bti-frame-cap">Fig. 01 — Training floor, on-site labs</span>
                        </div>

                        <div className="bti-panel">
                            <div className="bti-metrics">
                                <div className="bti-metrics-head">
                                    <span>Program Metrics</span>
                                    <span>Rev. A</span>
                                </div>
                                <div className="bti-metrics-grid">
                                    <div className="bti-metric">
                                        <span className="bti-metric-num">1000+</span>
                                        <span className="bti-metric-label">Engineers Trained</span>
                                    </div>
                                    <div className="bti-metric">
                                        <span className="bti-metric-num">30+</span>
                                        <span className="bti-metric-label">Bentley Tools Covered</span>
                                    </div>
                                    <div className="bti-metric">
                                        <span className="bti-metric-num">95%</span>
                                        <span className="bti-metric-label">Placement Record</span>
                                    </div>
                                </div>
                            </div>

                            <div className="bti-notes">
                                <p>
                                    Welcome to <strong>Bentley Authorized Training Center</strong> — your destination for mastering industry-leading infrastructure design and analysis tools like <b>STAAD Pro, OpenRoads, SewerGEMS, WaterGEMS, and MicroStation</b>.
                                </p>
                                <p>
                                    We empower engineers, students, and professionals to enhance their design and analysis capabilities through structured learning, real-world projects, and professional mentorship. Our mission is to build a new generation of skilled infrastructure engineers ready for global opportunities.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Annotations */}
                    <div className="bti-annotations">
                        <div className="bti-note-card">
                            <span className="bti-note-idx">01</span>
                            <h3>Industry-Focused Training</h3>
                            <p>Learn directly from certified professionals with years of real-world Bentley software experience.</p>
                        </div>
                        <div className="bti-note-card">
                            <span className="bti-note-idx">02</span>
                            <h3>Practical Learning</h3>
                            <p>Hands-on projects help you gain confidence in structural, civil, and water infrastructure design.</p>
                        </div>
                        <div className="bti-note-card">
                            <span className="bti-note-idx">03</span>
                            <h3>Career-Oriented Approach</h3>
                            <p>Our job assistance program ensures you are ready for global engineering opportunities.</p>
                        </div>
                    </div>

                    {/* Mission / Vision */}
                    <div className="bti-mv-grid">
                        <div className="bti-mv-panel dark">
                            <div className="bti-mv-head">
                                <span className="bti-mv-icon"><FiTarget /></span>
                                <span className="bti-mv-title">Mission</span>
                            </div>
                            <p>
                                To empower engineers and professionals with comprehensive Bentley software training,
                                bridging the gap between academic knowledge and industry requirements. We strive to
                                create skilled professionals who can tackle real-world infrastructure challenges with
                                confidence and expertise.
                            </p>
                            <div className="bti-mv-list">
                                <div className="bti-mv-row"><span className="bti-mv-check"><CheckIcon /></span>Industry-relevant curriculum</div>
                                <div className="bti-mv-row"><span className="bti-mv-check"><CheckIcon /></span>Hands-on practical training</div>
                                <div className="bti-mv-row"><span className="bti-mv-check"><CheckIcon /></span>Career development support</div>
                            </div>
                        </div>

                        <div className="bti-mv-panel dark">
                            <div className="bti-mv-head">
                                <span className="bti-mv-icon"><FiEye /></span>
                                <span className="bti-mv-title">Vision</span>
                            </div>
                            <p>
                                To become the leading Bentley training center globally, recognized for excellence in
                                infrastructure education and for producing world-class engineers who shape the future
                                of sustainable infrastructure development worldwide.
                            </p>
                            <div className="bti-mv-list">
                                <div className="bti-mv-row"><span className="bti-mv-check"><CheckIcon /></span>Global leadership in training</div>
                                <div className="bti-mv-row"><span className="bti-mv-check"><CheckIcon /></span>Innovation in education</div>
                                <div className="bti-mv-row"><span className="bti-mv-check"><CheckIcon /></span>Sustainable infrastructure focus</div>
                            </div>
                        </div>
                    </div>

                </div>
            </section>
        </>
    );
};

export default AboutOverview;